import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { EnvironmentConfigService } from '../services/environment-config.service';
import { TenantInfoDataService } from './tenant-info.data-service';

@Injectable({
    providedIn: 'root'
})
export class PublicSettingsDataService {
    /**
     * Service used to communicate with the portal api (no authentication required)
     * NOTE: Only available if a portal-api key is configured!
     */
    constructor(
        private readonly http: HttpClient,
        private readonly configService: EnvironmentConfigService,
        private tenantInfoService: TenantInfoDataService
    ) {}

    public getWebsiteSetting(settingKey: string): Observable<string> {
        return this.tenantInfoService.getTenantInfo().pipe(
            map(tenantInfo => tenantInfo.OrganisationName),
            switchMap(websiteName => {
                if (!websiteName) {
                    return of({ Value: '' });
                }
                const endpoint: string = `settings/${websiteName}/${settingKey}`;

                return this.performPortalApiGet(endpoint);
            }),
            map(setting => setting.Value.toString()),
            catchError(() => {
                console.error(`No setting found for key ${settingKey}. Returning empty string.`);
                return of('');
            })
        );
    }

    // #region Build Portal Api request
    private performPortalApiGet(endpoint: string, apiVersion: string = 'v2'): Observable<any> {
        return combineLatest([this.getPortalApiUrl(), this.getPortalApiKey()]).pipe(
            switchMap(([portalApiUrl, portalApiKey]) => {
                let params: HttpParams = new HttpParams();
                params = params.set('key', portalApiKey);

                const portalApiEndpoint: string = `${portalApiUrl}${apiVersion}/${endpoint}`;

                return this.http.get(portalApiEndpoint, { params });
            })
        );
    }

    private getPortalApiUrl(): Observable<string> {
        return this.configService
            .get('apiUrl')
            .pipe(map(apiUrl => apiUrl.replace('mediahaven-rest-api', 'portal-api')));
    }

    private getPortalApiKey(): Observable<string> {
        return this.tenantInfoService.getTenantInfo().pipe(
            map(tenantInfo => {
                let portalApiKey: string | undefined = tenantInfo.ApiKeys.find(
                    apiKey => apiKey.Name === `${tenantInfo.OrganisationName}_portal`
                )?.Value;

                if (!portalApiKey) {
                    portalApiKey = tenantInfo.OrganisationName;
                    console.error(`No portal api key found for organisation ${tenantInfo.OrganisationName}.`);
                }

                return portalApiKey;
            })
        );
    }
    // #endregion
}
